/**
 * C2 / GATE A — computer mutate ids for plan-approve (PR1 wired)
 *
 * Pure listing of sandbox / canvas-browser / desktop-computer tool function
 * names that are added to `PLAN_GATED_MUTATE_TOOL_IDS` (via propose-plan
 * spread) so tools-stream filterToolsForPlanGate + same-turn
 * isMutateToolGatedByPlan block them while awaiting_approval.
 *
 * Honesty boundaries:
 * - Status is `GATED` only because propose-plan includes the PR1 union and
 *   tools-stream already calls the live gate helpers (do not claim wired
 *   from this module alone without that compose)
 * - `isComputerMutateProposedForPlanGate` remains inventory match — live
 *   gate is still `isMutateToolGatedByPlan` in propose-plan.ts
 * - Desktop filesystem mutates are PR1.1 (separate inventory, also gated)
 * - Do not merge Private vault Approve into plan Approve
 *
 * Naming: OpenAI callable names are `${tool.toolName}__${function.id}`
 * (see `getEnabledToolFunctionName` in platformTools/toolEnablement.ts).
 *
 * Companions:
 *   LOSI_PERFECT_CREW_IMPLEMENTATION_PLAN (§C2 / GATE A)
 *   LOSI_ALWAYS_ON_AGENT_ROADMAP (M1 / T1)
 *   lib/chat/propose-plan.ts (Space + computer PR1 + filesystem PR1.1)
 *   __tests__/lib/crew-c2-computer-plan-gate-prep-lock.test.ts
 *
 * No Muse/Vesper. Preserve LosiAnimated.
 */

/** Live PR1 computer plan-gate — composed into PLAN_GATED_MUTATE_TOOL_IDS. */
export const CREW_C2_COMPUTER_PLAN_GATE_STATUS = 'GATED' as const

export type CrewC2ComputerPlanGateStatus =
  typeof CREW_C2_COMPUTER_PLAN_GATE_STATUS

/**
 * Sandbox / Losi Computer mutate callables that MUST be plan-gated for GATE A.
 * Includes getWorkspace (provisions/resumes a cloud computer — side-effect).
 * Source: sandboxTool.toolsFunctions (`toolName: sandbox`).
 */
export const CREW_C2_SANDBOX_MUTATE_TOOL_IDS = [
  'sandbox__runCode',
  'sandbox__getWorkspace',
  'sandbox__runCommand',
  'sandbox__writeFiles',
  'sandbox__publishFiles',
  'sandbox__startPreview',
  'sandbox__runAgentBrowser'
] as const

/**
 * Canvas browser mutate callables that MUST be plan-gated for GATE A.
 * Read helpers (status / list elements) stay available while awaiting approval
 * — same pattern as Space list/get tools.
 * Source: canvasBrowserTool (`toolName: canvasBrowser`).
 */
export const CREW_C2_CANVAS_BROWSER_MUTATE_TOOL_IDS = [
  'canvasBrowser__launchCanvasBrowser',
  'canvasBrowser__runCanvasBrowserAction'
] as const

/** Canvas browser reads that must remain ungated when PR1 lands. */
export const CREW_C2_CANVAS_BROWSER_READ_TOOL_IDS = [
  'canvasBrowser__getCanvasBrowserStatus',
  'canvasBrowser__listCanvasBrowserElements'
] as const

/**
 * Desktop filesystem mutates — gated in PR1.1 (same tools-stream path as PR1).
 * Source: desktopFilesystemTool (`toolName: DesktopFilesystemTool`).
 * Kept as a separate inventory so PR1 computer-control vs filesystem scopes
 * stay auditable; both spread into `PLAN_GATED_MUTATE_TOOL_IDS`.
 */
export const CREW_C2_DESKTOP_FILESYSTEM_MUTATE_TOOL_IDS = [
  'DesktopFilesystemTool__writeTextFile',
  'DesktopFilesystemTool__createTextFile',
  'DesktopFilesystemTool__createDirectory',
  'DesktopFilesystemTool__applyTextEdits',
  'DesktopFilesystemTool__duplicatePath',
  'DesktopFilesystemTool__movePath',
  'DesktopFilesystemTool__trashPath',
  'DesktopFilesystemTool__runSafeCommand'
] as const

/**
 * Desktop computer-control mutates that MUST be plan-gated for GATE A.
 * Source: desktopComputerControlTool (`toolName: DesktopComputerControlTool`).
 * Desktop filesystem write/trash ops live in
 * `CREW_C2_DESKTOP_FILESYSTEM_MUTATE_TOOL_IDS` (PR1.1 gated separately for audit).
 */
export const CREW_C2_DESKTOP_COMPUTER_MUTATE_TOOL_IDS = [
  'DesktopComputerControlTool__startSession',
  'DesktopComputerControlTool__advanceSession'
] as const

/**
 * Full gated set for C2 PR1 — union of sandbox + canvas + desktop computer
 * mutates. Wired into `PLAN_GATED_MUTATE_TOOL_IDS` via propose-plan spread
 * (compose with Private vault; do not merge vault Approve into plan Approve).
 * Does NOT include desktop filesystem mutates in this constant — those live
 * in `CREW_C2_DESKTOP_FILESYSTEM_MUTATE_TOOL_IDS` and are also spread into
 * PLAN_GATED in propose-plan (PR1.1).
 */
export const CREW_C2_PROPOSED_PLAN_GATED_COMPUTER_TOOL_IDS = [
  ...CREW_C2_SANDBOX_MUTATE_TOOL_IDS,
  ...CREW_C2_CANVAS_BROWSER_MUTATE_TOOL_IDS,
  ...CREW_C2_DESKTOP_COMPUTER_MUTATE_TOOL_IDS
] as const

export type CrewC2ProposedComputerPlanGateToolId =
  (typeof CREW_C2_PROPOSED_PLAN_GATED_COMPUTER_TOOL_IDS)[number]

export type CrewC2AdjacentComputerPlanGateToolId =
  (typeof CREW_C2_DESKTOP_FILESYSTEM_MUTATE_TOOL_IDS)[number]

/**
 * Platform tool catalog / family ids (not OpenAI function names).
 * Useful for doc/source locks; plan filter uses function names above.
 */
export const CREW_C2_COMPUTER_PLAN_GATE_FAMILY_IDS = [
  'sandbox',
  'canvas_browser',
  'desktop-computer-control-tool-v1',
  'desktop-filesystem-tool-v1'
] as const

/** Honesty banner — PR1 computer + PR1.1 filesystem gated via propose-plan. */
export const CREW_C2_COMPUTER_PLAN_GATE_HONESTY =
  'Computer plan-gate GATED — sandbox/canvas/desktop-computer (PR1) and desktop filesystem (PR1.1) mutates are in PLAN_GATED_MUTATE_TOOL_IDS (tools-stream filter + same-turn block).'

export function resolveCrewC2ComputerPlanGateStatus(): CrewC2ComputerPlanGateStatus {
  return CREW_C2_COMPUTER_PLAN_GATE_STATUS
}

export function isCrewC2ComputerPlanGateWired(): boolean {
  return true
}

/** PR1.1 — desktop filesystem mutates also live-gated. */
export function isCrewC2DesktopFilesystemPlanGateWired(): boolean {
  return true
}

export function listProposedComputerPlanGateToolIds(): readonly CrewC2ProposedComputerPlanGateToolId[] {
  return CREW_C2_PROPOSED_PLAN_GATED_COMPUTER_TOOL_IDS
}

/** Desktop-filesystem mutates — PR1.1 inventory (gated; kept separate for audit). */
export function listAdjacentComputerPlanGateToolIds(): readonly CrewC2AdjacentComputerPlanGateToolId[] {
  return CREW_C2_DESKTOP_FILESYSTEM_MUTATE_TOOL_IDS
}

function matchesToolIdInventory(
  toolId: string,
  inventory: readonly string[]
): boolean {
  for (const id of inventory) {
    if (id === toolId) {
      return true
    }
  }

  const bare = toolId.includes('__')
    ? toolId.split('__').pop() || toolId
    : toolId
  for (const id of inventory) {
    const proposedBare = id.includes('__') ? id.split('__').pop() || id : id
    if (proposedBare === bare) {
      return true
    }
  }

  return false
}

/**
 * Inventory check against the proposed PR1 set — NOT a substitute for the
 * live plan gate. Live gating remains `isMutateToolGatedByPlan` in
 * propose-plan.ts (which spreads this set into PLAN_GATED_MUTATE_TOOL_IDS).
 */
export function isComputerMutateProposedForPlanGate(
  toolId: unknown
): boolean {
  if (typeof toolId !== 'string' || !toolId.trim()) {
    return false
  }

  return matchesToolIdInventory(
    toolId.trim(),
    CREW_C2_PROPOSED_PLAN_GATED_COMPUTER_TOOL_IDS
  )
}

/**
 * Inventory check for adjacent desktop-filesystem mutates — NOT a live gate
 * and NOT part of the PR1 proposed union.
 */
export function isComputerMutateAdjacentForPlanGate(
  toolId: unknown
): boolean {
  if (typeof toolId !== 'string' || !toolId.trim()) {
    return false
  }

  return matchesToolIdInventory(
    toolId.trim(),
    CREW_C2_DESKTOP_FILESYSTEM_MUTATE_TOOL_IDS
  )
}
